import { useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, Switch, notification } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import APIClient from '../api/api';
import { FeatureFlag, UpdateFeatureFlagRequest } from '../api/models';
import { Button, Callout, StatusTag } from '../components/ui';
import { PageHead } from '../layout';
import {
  FeatureFlagGroup,
  groupFeatureFlags,
  isActionCapable,
  isSelfPaused,
  removeOverrideRow,
  upsertFlagRow,
} from './FeatureFlags.model';

interface PendingEnable {
  group: FeatureFlagGroup;
  /** Existing row being re-enabled, when there is one. */
  row?: FeatureFlag;
  scope: 'global' | 'account';
  accountKey: string;
}

interface PendingRemove {
  group: FeatureFlagGroup;
  row: FeatureFlag;
}

interface OverrideModalState {
  group: FeatureFlagGroup;
  existing?: FeatureFlag;
}

function flagLabel(group: FeatureFlagGroup, accountKey: string): string {
  const title = group.descriptor?.title ?? group.name;
  return accountKey === '' ? title : `${title} for ${accountKey}`;
}

function formatUpdated(row: FeatureFlag): string {
  const when = dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm');
  return row.updatedBy ? `${when} by ${row.updatedBy}` : when;
}

const FeatureFlags = () => {
  const [rows, setRows] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingEnable, setPendingEnable] = useState<PendingEnable | null>(null);
  const [enableReason, setEnableReason] = useState('');
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(null);
  const [overrideModal, setOverrideModal] = useState<OverrideModalState | null>(null);
  const [saving, setSaving] = useState(false);
  const [overrideForm] = Form.useForm();

  const groups = useMemo(() => groupFeatureFlags(rows), [rows]);

  useEffect(() => {
    loadFlags();
  }, []);

  async function loadFlags() {
    setLoading(true);
    try {
      setRows(await APIClient.listFeatureFlags());
    } catch (e) {
      notification.error({ message: 'Failed to load feature flags.' });
    } finally {
      setLoading(false);
    }
  }

  async function applyChange(req: UpdateFeatureFlagRequest, successMessage: string): Promise<boolean> {
    setSaving(true);
    try {
      const row = await APIClient.updateFeatureFlag(req);
      setRows((prev) => upsertFlagRow(prev, row));
      notification.success({ message: successMessage });
      return true;
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || 'Failed to update feature flag.';
      notification.error({ message: errorMsg });
      return false;
    } finally {
      setSaving(false);
    }
  }

  function handleToggle(group: FeatureFlagGroup, row: FeatureFlag | undefined, scope: 'global' | 'account', accountKey: string, next: boolean) {
    if (next) {
      setEnableReason('');
      setPendingEnable({ group, row, scope, accountKey });
      return;
    }
    applyChange(
      { name: group.name, scope, accountKey, enabled: false },
      `${flagLabel(group, accountKey)} disabled.`,
    );
  }

  async function handleConfirmEnable() {
    if (!pendingEnable) return;
    const { group, scope, accountKey } = pendingEnable;
    const ok = await applyChange(
      { name: group.name, scope, accountKey, enabled: true, reason: enableReason.trim() },
      `${flagLabel(group, accountKey)} enabled.`,
    );
    if (ok) setPendingEnable(null);
  }

  async function handleConfirmRemove() {
    if (!pendingRemove) return;
    const { group, row } = pendingRemove;
    setSaving(true);
    try {
      await APIClient.deleteFeatureFlagOverride({ name: group.name, accountKey: row.accountKey });
      setRows((prev) => removeOverrideRow(prev, group.name, row.accountKey));
      notification.success({ message: `Override for ${row.accountKey} removed.` });
      setPendingRemove(null);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setRows((prev) => removeOverrideRow(prev, group.name, row.accountKey));
        notification.info({ message: 'Override was already removed.' });
        setPendingRemove(null);
      } else {
        const errorMsg = e?.response?.data?.error || 'Failed to remove override.';
        notification.error({ message: errorMsg });
      }
    } finally {
      setSaving(false);
    }
  }

  function openOverrideModal(group: FeatureFlagGroup, existing?: FeatureFlag) {
    setOverrideModal({ group, existing });
    overrideForm.setFieldsValue({
      accountKey: existing?.accountKey ?? '',
      enabled: existing?.enabled ?? false,
      reason: existing?.reason ?? '',
    });
  }

  async function handleSaveOverride() {
    if (!overrideModal) return;
    const values = await overrideForm.validateFields();
    const accountKey: string = values.accountKey.trim();
    const ok = await applyChange(
      {
        name: overrideModal.group.name,
        scope: 'account',
        accountKey,
        enabled: !!values.enabled,
        reason: (values.reason ?? '').trim(),
      },
      `${flagLabel(overrideModal.group, accountKey)} saved.`,
    );
    if (ok) {
      setOverrideModal(null);
      overrideForm.resetFields();
    }
  }

  function renderRowMeta(row: FeatureFlag) {
    return (
      <div className="text-[11px] text-text-3">
        {isSelfPaused(row) && (
          <StatusTag tone="danger">self-paused</StatusTag>
        )}
        {!row.enabled && row.reason && (
          <span className="ml-1.5">paused: {row.reason}</span>
        )}
        <span className="ml-1.5">updated {formatUpdated(row)}</span>
      </div>
    );
  }

  function renderGroup(group: FeatureFlagGroup) {
    const actionCapable = isActionCapable(group.name);
    return (
      <div key={group.name} className="border border-border rounded-md bg-panel">
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <div className="flex items-center gap-2.5">
            <span className="text-[14px] font-semibold text-text">
              {group.descriptor?.title ?? group.name}
            </span>
            <span className="text-[11px] font-mono text-text-3">{group.name}</span>
            {actionCapable ? (
              <StatusTag tone="warn" dot={false}>action-capable</StatusTag>
            ) : (
              <StatusTag tone="neutral" dot={false}>shadow — log only</StatusTag>
            )}
          </div>
          {group.descriptor && (
            <div className="text-[12.5px] text-text-3 mt-0.5">
              Enables: {group.descriptor.description}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 flex items-center gap-3 border-b border-border">
          <Switch
            checked={group.global?.enabled ?? false}
            onChange={(next) => handleToggle(group, group.global, 'global', '', next)}
            size="small"
            aria-label={`${group.descriptor?.title ?? group.name} global`}
          />
          <span className="text-[12.5px] font-medium text-text">Global</span>
          {group.global ? (
            renderRowMeta(group.global)
          ) : (
            <span className="text-[11px] text-text-3">never set — off</span>
          )}
        </div>

        <div className="px-4 py-2.5">
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1.5">
            Account overrides
          </div>
          {group.overrides.length === 0 && (
            <div className="text-[12px] text-text-3 mb-1.5">
              No account overrides. All accounts follow the global row.
            </div>
          )}
          {group.overrides.map((row) => (
            <div key={row.accountKey} className="flex items-center gap-3 py-1.5">
              <Switch
                checked={row.enabled}
                onChange={(next) => handleToggle(group, row, 'account', row.accountKey, next)}
                size="small"
                aria-label={`${group.descriptor?.title ?? group.name} for ${row.accountKey}`}
              />
              <span className="text-[12.5px] font-mono text-text">{row.accountKey}</span>
              {renderRowMeta(row)}
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<EditOutlined />}
                className="ml-auto"
                onClick={() => openOverrideModal(group, row)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<DeleteOutlined />}
                onClick={() => setPendingRemove({ group, row })}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<PlusOutlined />}
            onClick={() => openOverrideModal(group)}
          >
            Add account override
          </Button>
        </div>
      </div>
    );
  }

  const pendingSelfPaused = pendingEnable?.row ? isSelfPaused(pendingEnable.row) : false;
  const removeFallbackEnables =
    !!pendingRemove && !pendingRemove.row.enabled && !!pendingRemove.group.global?.enabled;

  return (
    <>
      <PageHead
        title="Feature Flags"
        subtitle="Kill switches for the fast transfer auto-opt-in pipeline. A global row switches a subsystem everywhere; an account row overrides it for one registrar or seller account. For most gated actions both must be enabled."
      />
      <div className="flex-1 min-h-0 overflow-auto bg-panel p-6">
        <div className="mb-4">
          <Callout tone="warn" title="Check alerts before re-enabling">
            Subsystems switch their own flag off when they detect an anomaly and record an
            alert explaining why. Re-enabling a flag without reading that alert defeats the
            interlock. <Link to="/alerts" className="underline">Open alerts</Link>
          </Callout>
        </div>
        {loading && rows.length === 0 && (
          <div className="text-center py-12 text-text-3 text-[12px]">Loading…</div>
        )}
        <div className="flex flex-col gap-4">
          {groups.map(renderGroup)}
        </div>
      </div>

      <Modal
        title={pendingEnable ? `Enable ${flagLabel(pendingEnable.group, pendingEnable.accountKey)}?` : 'Enable flag?'}
        open={!!pendingEnable}
        onCancel={() => setPendingEnable(null)}
        onOk={handleConfirmEnable}
        okText="Enable"
        confirmLoading={saving}
        width={480}
      >
        {pendingEnable && (
          <div className="flex flex-col gap-3">
            {pendingEnable.group.descriptor && (
              <div className="text-[12.5px] text-text-2">
                Enables: {pendingEnable.group.descriptor.description}
              </div>
            )}
            {pendingSelfPaused ? (
              <Callout tone="danger" title="This flag paused itself">
                {pendingEnable.row?.reason
                  ? `Recorded reason: ${pendingEnable.row.reason}`
                  : 'An anomaly was detected.'}{' '}
                Read the matching alert before re-enabling.{' '}
                <Link to="/alerts" className="underline">Open alerts</Link>
              </Callout>
            ) : (
              <Callout tone="warn">
                {pendingEnable.row?.reason
                  ? `This flag is off with a recorded reason: ${pendingEnable.row.reason}. `
                  : 'If this flag was switched off automatically, an alert explains why. '}
                Check <Link to="/alerts" className="underline">alerts</Link> before enabling.
              </Callout>
            )}
            {isActionCapable(pendingEnable.group.name) && (
              <div className="text-[12px] text-text-3">
                This flag is action-capable: once enabled, the subsystem acts on live
                registrar or marketplace state.
              </div>
            )}
            <Input
              placeholder="Reason (recorded in the audit log)"
              value={enableReason}
              onChange={(e) => setEnableReason(e.target.value)}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={pendingRemove ? `Remove override for ${pendingRemove.row.accountKey}?` : 'Remove override?'}
        open={!!pendingRemove}
        onCancel={() => setPendingRemove(null)}
        onOk={handleConfirmRemove}
        okText="Remove"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
        width={480}
      >
        {pendingRemove && (
          <div className="flex flex-col gap-3">
            <div className="text-[12.5px] text-text-2">
              {pendingRemove.row.accountKey} falls back to the global row, which is
              currently {pendingRemove.group.global?.enabled ? 'enabled' : 'off'}.
            </div>
            {removeFallbackEnables && (
              <Callout tone="danger" title="Removing this override enables the flag">
                {`${flagLabel(pendingRemove.group, pendingRemove.row.accountKey)} is off only
                because of this override. `}
                {isSelfPaused(pendingRemove.row)
                  ? 'It was written by an auto-pause trigger — read the matching alert before removing it. '
                  : ''}
                <Link to="/alerts" className="underline">Open alerts</Link>
              </Callout>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={
          overrideModal
            ? overrideModal.existing
              ? `Edit override: ${flagLabel(overrideModal.group, overrideModal.existing.accountKey)}`
              : `Add account override: ${overrideModal.group.descriptor?.title ?? overrideModal.group.name}`
            : 'Account override'
        }
        open={!!overrideModal}
        onCancel={() => {
          setOverrideModal(null);
          overrideForm.resetFields();
        }}
        onOk={handleSaveOverride}
        okText="Save"
        confirmLoading={saving}
        width={480}
      >
        {overrideModal && (
          <Form form={overrideForm} layout="vertical">
            <p className="text-text-3 text-[12px] mb-4">
              An account override wins over the global row for that account only. Remove the
              override to hand the account back to the global row.
            </p>
            <Form.Item
              label="Account key"
              name="accountKey"
              rules={[{ required: true, whitespace: true, message: 'Account key required' }]}
            >
              <Input
                placeholder="registrar or seller account key"
                disabled={!!overrideModal.existing}
              />
            </Form.Item>
            <Form.Item label="Enabled" name="enabled" valuePropName="checked">
              <Switch size="small" />
            </Form.Item>
            <Form.Item label="Reason (recorded in the audit log)" name="reason">
              <Input placeholder="why this account differs from the global row" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default FeatureFlags;
